import * as React from 'react';
import { EquipmentListContext } from '../ReactContexts/equipmentListContext';
import { GlobalErrorContext } from '../ReactContexts/globalErrorContext';
import { PushNotificationContext } from '../ReactContexts/pushNotificationContext';
import { isAbortError } from 'errors/isAbortError';
import { notificationsApi } from 'services/api/notifications/queries';

export function useNotificationsTopRight() {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isUpdateHoursExpanded, setIsUpdateHoursExpanded] =
    React.useState(false);

  const { equipmentListData } = React.useContext(EquipmentListContext);
  const { pushNotifications } = React.useContext(PushNotificationContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const {
    data: inbox = [],
    error: inboxError,
    isError: isInboxError,
    refetch,
  } = notificationsApi.useGetInboxQuery();

  const isCancelledRequestError = React.useCallback((error: unknown) => {
    if (isAbortError(error)) return true;

    const typedError = error as { status?: string };
    return typedError?.status === 'CANCELED';
  }, []);

  React.useEffect(() => {
    if (!isInboxError) {
      return;
    }

    if (isCancelledRequestError(inboxError)) {
      return;
    }

    addErrorModal();
  }, [isInboxError, inboxError, addErrorModal, isCancelledRequestError]);

  React.useEffect(() => {
    if (pushNotifications.length === 0) {
      return;
    }

    (async () => {
      try {
        const latestPushNotificationId =
          pushNotifications[pushNotifications.length - 1];
        if (
          !inbox.some(
            notification => notification.id == latestPushNotificationId,
          )
        ) {
          const refreshedInboxResult = await refetch();
          if ('error' in refreshedInboxResult && refreshedInboxResult.error) {
            throw refreshedInboxResult.error;
          }
        }
        setIsExpanded(true);
      } catch (error) {
        if (isCancelledRequestError(error)) return;

        addErrorModal();
      }
    })();
  }, [
    pushNotifications,
    inbox,
    refetch,
    addErrorModal,
    isCancelledRequestError,
  ]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (event.target.className === 'k-overlay') {
        event.stopPropagation();
        setIsUpdateHoursExpanded(false);
      }
    }

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const unreadNotificationsCount = React.useMemo(() => {
    return inbox.filter(notification => !notification.isRead).length;
  }, [inbox]);

  const shouldHideNotifications = React.useMemo(() => {
    return (
      equipmentListData.equipmentList == undefined &&
      window.location.hash.startsWith('#/equipment')
    );
  }, [equipmentListData.equipmentList]);

  const toggleExpanded = React.useCallback(() => {
    setIsExpanded(current => !current);
  }, []);

  const updateIsExpanded = React.useCallback((updatedIsExpanded: boolean) => {
    setIsExpanded(updatedIsExpanded);
  }, []);

  const updateIsUpdateHoursExpanded = React.useCallback(
    (updatedIsUpdateHoursExpanded: boolean) => {
      setIsUpdateHoursExpanded(updatedIsUpdateHoursExpanded);
    },
    [],
  );

  return {
    inbox,
    isExpanded,
    isUpdateHoursExpanded,
    shouldHideNotifications,
    unreadNotificationsCount,
    toggleExpanded,
    updateIsExpanded,
    updateIsUpdateHoursExpanded,
  };
}
